import React, { Suspense, useEffect, useState, useRef } from 'react';
import { Canvas, useFrame, useLoader } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, Html, PointerLockControls } from '@react-three/drei';
import { api, useServer } from '../contexts/ServerContext';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import toast from 'react-hot-toast';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';

const MAP_SIZE = 32768;
const SCALE = 1;
const VISUAL_SIZE = MAP_SIZE * SCALE;

const CAMERA_START = [-7994.394601855742, 11537.144628563468, 15087.411703754713]; 
const CAMERA_TARGET = [41, 86, 0];

function PlayerBall({ steamId, pos, color, name, messages, faction }) {
  // Преобразую RGB в hex
  const rgbToHex = (c) => {
    if (!c) return '#ffeb3b';
    const r = Math.max(0, Math.min(255, c.r)).toString(16).padStart(2, '0');
    const g = Math.max(0, Math.min(255, c.g)).toString(16).padStart(2, '0');
    const b = Math.max(0, Math.min(255, c.b)).toString(16).padStart(2, '0');
    return `#${r}${g}${b}`;
  };
  const playerColor = rgbToHex(color);
  // Определяем путь к иконке фракции
  let factionIcon = '/icons/neutral.png';
  if (faction === 'slayer') factionIcon = '/icons/slayer.png';
  else if (faction === 'demon') factionIcon = '/icons/demon.png';
  return (
    <group position={[
      pos.x * SCALE,
      pos.z * SCALE,
      -pos.y * SCALE
    ]} rotation={[Math.PI, 0, 0]}>
      <mesh>
        <coneGeometry args={[VISUAL_SIZE * 0.001, VISUAL_SIZE * 0.003, 16]} />
        <meshStandardMaterial color={playerColor} />
      </mesh>

      {messages && messages.length > 0 && (
        <>
          {messages.toReversed().map((msg, idx) => (
            <Html
              key={idx}
              position={[0, VISUAL_SIZE * 0.003 - 260 -idx * 30, 0]}
              center
              distanceFactor={14}
              style={{
                background: 'rgba(0,0,0,0.85)',
                color: '#fff',
                padding: '2px 12px',
                borderRadius: 6,
                fontSize: 800,
                marginBottom: 4,
                border: '1px solid var(--color-primary, #dc255a)',
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
                textShadow: '0 0 8px #000, 0 0 4px #000',
                WebkitTextStroke: '1px #fff',
                textStroke: '1px #fff',
                pointerEvents: 'none',
              }}
            >
              {msg}
            </Html>
          ))}
        </>
      )}


      <Html position={[0, VISUAL_SIZE * 0.003 - 220, 0]} center distanceFactor={14} style={{ 
        color: '#fff',
        padding: '50px 120px',
        borderRadius: 80,
        fontSize: 400,
        fontWeight: 700,
        fontFamily: 'Montserrat, Arial, sans-serif',
        whiteSpace: 'nowrap',
        letterSpacing: 2,
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        width: 'max-content',
      }}>
        <img src={factionIcon} alt={faction} style={{ width: 4000, height: 4000, filter: 'drop-shadow(0 0 8px #000)' }} />
      </Html>

    <Html position={[0, VISUAL_SIZE * 0.003 - 100, 0]} center distanceFactor={14} style={{
      background: 'rgb(18, 18, 18)',
      color: '#fff',
      padding: '50px 120px',
      borderRadius: 80,
      fontSize: 400,
      fontWeight: 700,
      fontFamily: 'Montserrat, Arial, sans-serif',
      whiteSpace: 'nowrap',
      border: '10px solid rgb(46, 46, 46)',
      boxShadow: '0 4px 64px 0 var(--color-primary-alpha, rgba(220, 37, 90, 0.294)), 0 0 20px 0px #fff2',
      letterSpacing: 2,
      textShadow: '0 2px 12px #000, 0 0 2px var(--color-primary, #dc255a)',
      WebkitTextStroke: '1.5px #fff',
      textStroke: '1.5px #fff',
      pointerEvents: 'none',
      display: 'flex',
      alignItems: 'center',
      gap: 120,
      width: 'max-content',
    }}>
      <span>{name ? `${name} (${steamId})` : steamId}</span>
    </Html>
    </group>
  );
}

function CustomPointerLockFly({ enabled, canvasRef }) {
  const { camera } = useThree();
  const move = useRef({ forward: false, back: false, left: false, right: false });
  const shiftPressed = useRef(false);
  const controlsRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Shift') shiftPressed.current = true;
      if (e.code === 'KeyW') move.current.forward = true;
      if (e.code === 'KeyS') move.current.back = true;
      if (e.code === 'KeyA') move.current.left = true;
      if (e.code === 'KeyD') move.current.right = true;
    };
    const handleKeyUp = (e) => {
      if (e.key === 'Shift') shiftPressed.current = false;
      if (e.code === 'KeyW') move.current.forward = false;
      if (e.code === 'KeyS') move.current.back = false;
      if (e.code === 'KeyA') move.current.left = false;
      if (e.code === 'KeyD') move.current.right = false;
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    const handlePointerLockError = () => {
      toast.error('Браузер временно блокирует повторный вход в режим управления после ESC. Переключитесь в другое окно или подождите пару секунд.');
    };
    
    const handlePointerLockChange = () => {
      // Если указатель разблокирован, сбрасываем состояние
      if (!document.pointerLockElement) {
        move.current = { forward: false, back: false, left: false, right: false };
        shiftPressed.current = false;
      }
    };

    const handleVisibilityChange = () => {
      // При переключении вкладок сбрасываем состояние
      if (document.hidden) {
        move.current = { forward: false, back: false, left: false, right: false };
        shiftPressed.current = false;
        if (document.pointerLockElement) {
          document.exitPointerLock();
        }
      }
    };

    document.addEventListener('pointerlockerror', handlePointerLockError);
    document.addEventListener('pointerlockchange', handlePointerLockChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      document.removeEventListener('pointerlockerror', handlePointerLockError);
      document.removeEventListener('pointerlockchange', handlePointerLockChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useFrame((_, delta) => {
    if (!enabled || !document.pointerLockElement) return;
    const speed = shiftPressed.current ? 8000 : 2000;
    let dir = new THREE.Vector3();
    if (move.current.forward) dir.z -= 1;
    if (move.current.back) dir.z += 1;
    if (move.current.left) dir.x -= 1;
    if (move.current.right) dir.x += 1;
    if (dir.lengthSq() > 0) {
      dir.normalize();
      dir.applyQuaternion(camera.quaternion);
      camera.position.addScaledVector(dir, speed * delta);
    }
  });

  return (
    <PointerLockControls
      ref={controlsRef}
      enabled={enabled}
      selector={null}
      makeDefault={true}
      onUpdate={() => {}}
      onLock={() => {
        console.log('PointerLock активирован');
      }}
      onUnlock={() => {
        console.log('PointerLock деактивирован');
        // Сбрасываем состояние движения при разблокировке
        move.current = { forward: false, back: false, left: false, right: false };
        shiftPressed.current = false;
      }}
    />
  );
}


function MapModel({ setError, onCenter, mapPosition, firstPlayerPos, shiftDebugSpheres }) {
  const obj = useLoader(OBJLoader, '/map/map.obj');
  const texture = useLoader(THREE.TextureLoader, '/map/texture.png');
  const [center, setCenter] = React.useState([0,0,0]);
  React.useEffect(() => {
    if (!obj) setError('OBJ не загружен');
    if (!texture) setError('Текстура не загружена');
    if (obj) {
      const box = new THREE.Box3().setFromObject(obj);
      const c = new THREE.Vector3();
      box.getCenter(c);
      const size = new THREE.Vector3();
      box.getSize(size);
      setCenter([c.x, c.y, c.z]);
      if (onCenter) onCenter([c.x, c.y, c.z]);
    }
  }, [obj, texture, setError, onCenter]);
  if (!obj || !mapPosition) return null;
  obj.traverse((child) => {
    if (child.isMesh && texture) {
      child.material.map = texture;
      child.material.needsUpdate = true;
    }
  });
  return (
    <group position={mapPosition} rotation={[0, Math.PI, 0]}>
      <primitive object={obj} />
    </group>
  );
}

function CameraSetup({ enabled, position, target, controlsRef, controlMode }) {
  const { camera } = useThree();
  useEffect(() => {
    if (!enabled) return;
    camera.position.set(...position);
    camera.lookAt(...target);
    camera.updateProjectionMatrix();
    if (controlMode === 'orbit' && controlsRef.current) {
      controlsRef.current.target.set(...target);
      controlsRef.current.update();
    }
  }, [enabled, position, target, camera, controlsRef, controlMode]);
  return null;
}

export default function Map3D() {
  const [players, setPlayers] = useState([]);
  const [error, setError] = useState(null);
  const [contextLost] = useState(false);
  const [controlMode, setControlMode] = useState('fly');
  const canvasRef = useRef(null);

  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapCenter, setMapCenter] = useState([0,0,0]);
  const [mapPosition, setMapPosition] = useState(null);
  const [mapShift] = useState([-105, -1150, 1500]);
  const [shiftDebugSpheres] = useState(true);
  const firstPlayerPos = players.length > 0 ? [players[0].pos.x * SCALE, players[0].pos.z * SCALE, -players[0].pos.y * SCALE] : null;
  const [cameraPosition, setCameraPosition] = useState([0,0,0]);

  const { server } = useServer();

  function CameraPositionTracker() {
    const { camera } = useThree();
    useFrame(() => {
      setCameraPosition([camera.position.x, camera.position.y, camera.position.z]);
    });
    return null;
  }

  const handleMapCenter = React.useCallback((center) => {
    if (!mapPosition) {
      setMapPosition([
        -center[0] + mapShift[0],
        -center[1] + mapShift[1],
        -center[2] + mapShift[2]
      ]);
      setMapLoaded(true);
    }
    setMapCenter(center);
  }, [mapPosition, mapShift]);

  useEffect(() => {
    let mounted = true;
    const fetchPlayers = async () => {
      try {
        const res = await api.get(`/api/map/players`);
        if (mounted && res.data && res.data.players) {
          const arr = Object.entries(res.data.players).map(([steamId, v]) => ({
            steamId,
            pos: v.Vector || v.pos || { x: 0, y: 0, z: 0 },
            color: v.Color || v.color || { r: 255, g: 255, b: 255 },
            name: v.Name || v.name || '',
            messages: v.Messages || [],
            faction: v.faction || v.Faction || 'neutral',
          }));
          setPlayers(arr);
        }
      } catch {
        if (mounted) setPlayers([]);
      }
    };
    fetchPlayers();
    const interval = setInterval(fetchPlayers, 5000);
    
    // Очистка при размонтировании
    return () => { 
      mounted = false; 
      clearInterval(interval);
      // Снимаем PointerLock при размонтировании
      if (document.pointerLockElement) {
        document.exitPointerLock();
      }
    };
  }, [server]);

  const cameraRef = useRef();
  const orbitRef = useRef();

  useEffect(() => {
    if (!mapLoaded) return;
    if (controlMode === 'orbit' && orbitRef.current && cameraRef.current) {
      setTimeout(() => {
        cameraRef.current.position.set(...CAMERA_START);
        orbitRef.current.target.set(...CAMERA_TARGET);
        orbitRef.current.update();
        cameraRef.current.lookAt(...CAMERA_TARGET);
        cameraRef.current.updateProjectionMatrix();
      }, 0);
    }
    if (controlMode === 'fly' && cameraRef.current) {
      cameraRef.current.position.set(...CAMERA_START);
      cameraRef.current.lookAt(...CAMERA_TARGET);
      cameraRef.current.updateProjectionMatrix();
    }
  }, [controlMode, mapLoaded]);

  if (error) {
    return (
      <div className="w-full h-[80vh] flex items-center justify-center bg-black rounded-lg border border-gmod-lighterer mt-6">
        <div className="text-gmod-white opacity-60 text-xl">{error}</div>
      </div>
    );
  }

  if (contextLost) {
    return (
      <div className="w-full h-[80vh] flex items-center justify-center bg-black rounded-lg border border-gmod-lighterer mt-6">
        <div className="text-gmod-white opacity-60 text-xl">WebGL контекст потерян. Перезагрузите страницу.</div>
      </div>
    );
  }

  return (
    <div className="w-full h-[80vh] bg-black rounded-lg border border-gmod-lighterer mt-6 relative select-none">

      <div className="absolute top-4 right-6 z-20 flex gap-2">
        <button
          className={`px-3 py-1 rounded-md text-sm font-semibold border transition-colors duration-150 ${controlMode === 'orbit' ? 'bg-theme-primary text-white border-theme-primary' : 'bg-gmod-lighter text-gmod-white border-gmod-lighterer hover:bg-theme-primary hover:text-white'}`}
          onClick={() => setControlMode('orbit')}
        >
          Orbit (мышь)
        </button>
        <button
          className={`px-3 py-1 rounded-md text-sm font-semibold border transition-colors duration-150 ${controlMode === 'fly' ? 'bg-theme-primary text-white border-theme-primary' : 'bg-gmod-lighter text-gmod-white border-gmod-lighterer hover:bg-theme-primary hover:text-white'}`}
          onClick={() => setControlMode('fly')}
        >
          Fly (WASD)
        </button>
      </div>
      <Canvas
        ref={canvasRef}
        style={{ background: '#111' }}
        shadows
        onClick={() => {
          if (controlMode === 'fly') {
            const canvas = document.querySelector('canvas');
            if (canvas && canvas.isConnected && document.body.contains(canvas)) {
              try {
                canvas.requestPointerLock();
              } catch (error) {
                console.warn('Не удалось запросить PointerLock:', error);
              }
            }
          }
        }}
      >
        <Suspense fallback={null}>
          <PerspectiveCamera
            ref={cameraRef}
            makeDefault
            position={CAMERA_START}
            fov={60}
            far={200000}
          />
          <CameraSetup enabled={mapLoaded} position={CAMERA_START} target={CAMERA_TARGET} controlsRef={orbitRef} controlMode={controlMode} />
          <ambientLight intensity={0.5} />
          <directionalLight position={[0, 100, 100]} intensity={0.5} />
          <MapModel setError={setError} onCenter={handleMapCenter} mapPosition={mapPosition} firstPlayerPos={firstPlayerPos} shiftDebugSpheres={shiftDebugSpheres} />
          {controlMode === 'orbit' ? (
            <OrbitControls ref={orbitRef} maxDistance={2000} minDistance={5} />
          ) : (
            <CustomPointerLockFly enabled={controlMode === 'fly'} canvasRef={canvasRef} />
          )}
          <CameraPositionTracker />
          {mapLoaded && players.map((p) => (
            <PlayerBall key={p.steamId} steamId={p.steamId} pos={p.pos} color={p.color} name={p.name} messages={p.messages} faction={p.faction} />
          ))}
        </Suspense>
      </Canvas>
    </div>
  );
} 